import axios from "axios";
import sha256 from "js-sha256";
import { getEnvironmentConfig } from "../utils/environment";

export const api = async function (endpoint, type, data) {
  const envConfig = getEnvironmentConfig();
  const origin = envConfig.api.apiUrl;
  let request = await axios({
    method: type,
    url: origin + endpoint,
    data: data,
  });
  return request;
};

const CONVERSION_SENT_PREFIX = "ccw_conversion_sent_";

export function hasConversionBeenSent(bookingId) {
  if (!bookingId) return false;
  try {
    return localStorage.getItem(CONVERSION_SENT_PREFIX + String(bookingId)) === "1";
  } catch (e) {
    return false;
  }
}

export function markConversionSent(bookingId) {
  if (!bookingId) return;
  try {
    localStorage.setItem(CONVERSION_SENT_PREFIX + String(bookingId), "1");
  } catch (e) {
    // Private mode / quota — in-memory guards on the success pages still apply.
  }
}

/**
 * Service total + post-booking extras (e.g. leather treatment on /booking-extras).
 * cart.pricing.total is frozen at payment time and does not include extras added later.
 */
export function getBookingConversionValue(booking, cart) {
  let serviceTotal = 0;

  if (
    booking?.estimatedTotal != null &&
    !Number.isNaN(Number(booking.estimatedTotal))
  ) {
    serviceTotal = Number(booking.estimatedTotal);
  } else if (
    cart?.pricing?.total != null &&
    !Number.isNaN(Number(cart.pricing.total))
  ) {
    serviceTotal = Number(cart.pricing.total);
  } else if (cart?.step2Data?.selectedServices?.[0]?.price != null) {
    serviceTotal = Number(cart.step2Data.selectedServices[0].price) || 0;
  } else if (cart?.step2Data?.selectedBundle?.price != null) {
    serviceTotal = Number(cart.step2Data.selectedBundle.price) || 0;
  } else if (booking?.cart?.sessionData?.estimatedTotal != null) {
    serviceTotal = Number(booking.cart.sessionData.estimatedTotal) || 0;
  } else if (cart?.sessionData?.estimatedTotal != null) {
    serviceTotal = Number(cart.sessionData.estimatedTotal) || 0;
  }

  let extrasTotal = 0;
  if (Array.isArray(booking?.selectedExtras) && booking.selectedExtras.length > 0) {
    extrasTotal = booking.selectedExtras.reduce(
      (sum, extra) => sum + (Number(extra.price) || 0),
      0,
    );
  } else if (Number(cart?.pricing?.extrasPrice) > 0) {
    extrasTotal = Number(cart.pricing.extrasPrice);
  }

  const total = serviceTotal + extrasTotal;
  return Math.round(total * 100) / 100;
}

function appendExtraLineItems(items, booking) {
  if (!Array.isArray(booking?.selectedExtras)) {
    return items;
  }
  const next = [...items];
  for (const extra of booking.selectedExtras) {
    next.push({
      item_name: extra.name || "Extra",
      item_id: extra.posKey || extra.id || extra.name || "",
      quantity: 1,
      price: Number(extra.price) || 0,
    });
  }
  return next;
}

function pushBookingConfirmedToGtm(GTM, payload) {
  if (!GTM) return;

  const { transactionId, value, extraEventProps } = payload;
  const posId = String(transactionId);

  GTM.dataLayer({
    dataLayer: {
      event: "booking_confirmed",
      iPosId: posId,
      bookingNumber: posId,
      transaction_id: posId,
      value: Number(value) || 0,
      currency: "AUD",
      eventProps: {
        value: {
          ...extraEventProps,
          bookingNumber: posId,
          iPosId: posId,
          transaction_id: posId,
          value: Number(value) || 0,
          currency: "AUD",
        },
      },
    },
  });
}

/**
 * GTM purchase + booking_confirmed, and ReactGA booking_confirmed (previous setup).
 */
function pushPurchaseToGtm(GTM, payload) {
  if (!GTM) return;

  const {
    transactionId,
    value,
    items,
    couponCode,
    discount,
    rewardCode,
    offerCode,
    userEmail,
    extraEventProps,
  } = payload;

  const dataLayer = {
    event: "purchase",
    ecommerce: {
      transaction_id: String(transactionId),
      currency: "AUD",
      value: Number(value) || 0,
      items: items || [],
    },
    iPosId: transactionId,
    bookingNumber: transactionId,
    eventProps: {
      value: {
        ...extraEventProps,
        bookingNumber: transactionId,
        iPosId: transactionId,
      },
    },
  };

  if (couponCode) {
    dataLayer.ecommerce.coupon = couponCode;
  }
  if (discount !== "" && discount != null) {
    dataLayer.ecommerce.discount = discount;
  }
  if (rewardCode) {
    dataLayer.ecommerce.reward = rewardCode;
  }
  if (offerCode) {
    dataLayer.ecommerce.offer = offerCode;
  }

  if (userEmail) {
    const normalize = (s) => s.trim().toLowerCase();
    dataLayer.user_data = {
      sha256_email_address: sha256(normalize(userEmail)),
    };
  }

  // Clear prior ecommerce object so items do not merge across hits
  GTM.dataLayer({ dataLayer: { ecommerce: null } });
  GTM.dataLayer({ dataLayer });
}

export const googleEvent = async function (ReactGA, GTM, event, value) {
  if (event === "booking_confirmed") {
    let booking = null;
    let isNewBookingFlow = false;

    try {
      booking = await api(
        `booking-flow/booking/${value.bookingNumber}`,
        "GET",
        null,
      );
      if (booking.status === 200 && booking.data.booking) {
        isNewBookingFlow = true;
      }
    } catch (error) {
      try {
        booking = await api(`booking/${value.bookingNumber}`, "GET", null);
        isNewBookingFlow = false;
      } catch (oldError) {
        console.error("Error fetching booking for event tracking:", oldError);
        const fallbackId = value.iPosId || value.bookingNumber;
        if (GTM && fallbackId) {
          pushBookingConfirmedToGtm(GTM, {
            transactionId: fallbackId,
            value: 0,
            extraEventProps: value,
          });
          pushPurchaseToGtm(GTM, {
            transactionId: fallbackId,
            value: 0,
            items: [],
            extraEventProps: value,
          });
        }
        if (ReactGA) {
          ReactGA.event(event, {
            ...value,
            bookingNumber: fallbackId,
            iPosId: fallbackId,
            transaction_id: String(fallbackId),
            value: 0,
            currency: "AUD",
          });
        }
        return;
      }
    }

    if (booking && booking.status === 200) {
      let data = booking.data.booking;
      let cart = booking.data.cart;
      let items = [];
      let estimatedTotal = 0;
      let userEmail = "";
      let discount = "";
      let couponCode = "";
      let rewardCode = "";
      let offerCode = "";

      const iPosId =
        data?.posBookingKey ||
        data?.bookingKey ||
        data?.posResponse?.BookingKey ||
        value.iPosId ||
        value.bookingNumber;

      if (isNewBookingFlow && cart) {
        if (
          cart.step2Data?.selectedServices &&
          cart.step2Data.selectedServices.length > 0
        ) {
          for (let i = 0; i < cart.step2Data.selectedServices.length; i++) {
            const element = cart.step2Data.selectedServices[i];
            items.push({
              item_name: element.name || "Service",
              item_id: element.id || element._id || "",
              quantity: element.quantity || 1,
            });
          }
        } else if (cart.step2Data?.selectedBundle) {
          const bundle = cart.step2Data.selectedBundle;
          items.push({
            item_name: bundle.name || "Service Bundle",
            item_id: bundle.id || bundle._id || "",
            quantity: 1,
          });
        }

        userEmail =
          cart.step1Data?.email || cart.step3Data?.email || cart.email || "";

        discount = cart.pricing?.discount || cart.discount || "";
        couponCode = cart.couponDetails?.code || cart.couponCode || "";
        rewardCode = cart.rewardCode || "";
        offerCode = cart.offerCode || "";
      } else {
        if (
          data.cart &&
          data.cart.sessionData &&
          data.cart.sessionData.selectedServiceId
        ) {
          for (
            let i = 0;
            i < data.cart.sessionData.selectedServiceId.length;
            i++
          ) {
            const element = data.cart.sessionData.selectedServiceId[i];
            items.push({
              item_name: element.name,
              item_id: element.id,
              quantity: element.quantity,
            });
          }
        }

        userEmail = data.posResponse?.BookingEmail || "";
        discount = data.cart?.sessionData?.discount || "";
        couponCode = data.cart?.sessionData?.couponCode || "";
        rewardCode = data.cart?.sessionData?.rewardCode || "";
        offerCode = data.cart?.sessionData?.offerCode || "";
      }

      items = appendExtraLineItems(items, data);
      estimatedTotal = getBookingConversionValue(data, cart);

      pushBookingConfirmedToGtm(GTM, {
        transactionId: iPosId,
        value: estimatedTotal,
        extraEventProps: value,
      });

      pushPurchaseToGtm(GTM, {
        transactionId: iPosId,
        value: estimatedTotal,
        items,
        couponCode,
        discount,
        rewardCode,
        offerCode,
        userEmail,
        extraEventProps: value,
      });

      if (ReactGA) {
        const posId = String(iPosId);
        ReactGA.event(event, {
          ...value,
          bookingNumber: posId,
          iPosId: posId,
          transaction_id: posId,
          value: estimatedTotal,
          currency: "AUD",
        });
      }
    }
  } else {
    if (ReactGA) {
      ReactGA.event(event, value);
    }
  }
};
